<template>
  <modal
    name="modal--submit-order"
    transition="nice-modal-fade"
    :click-to-close="false"
    :width="560"
    :height="'auto'"
    :adaptive="true"
    :reset="true"
    :scrollable="true"
    @before-open="beforeOpen"
    @before-close="beforeClose"
    class="py-10"
  >
    <div
      class="flex flex-col text-black h-full bg-white overflow-hidden w-full justify-between p-4"
    >
      <div v-if="loading === false" class="w-full">
        <div class="text-center text-4xl mb-1 font-extralight tracking-tight">
          Confirm Orders
        </div>

        <div
          v-if="Object.keys(invoices).length"
          class="text-justify text-xl mb-6 font-extralight tracking-tight leading-tight"
        >
          For each order, adjust product amounts to meet minimum order or select
          cancel order.
        </div>
        <div v-if="!Object.keys(invoices).length">Are you sure?</div>
        <div
          v-for="invoice in invoices"
          :key="invoice.vendorId"
          class="mb-2 px-4 py-1 text-lg tracking-tight bg-gray-300 rounded-lg"
          :class="
            activeInvoiceId == invoice.vendorId
              ? 'shadow-outline'
              : invoice.discrepancy < 0 || invoice.caseDiscrepancy < 0
              ? 'cursor-pointer hover:bg-gray-400'
              : ''
          "
          @click="invoiceClick(invoice.vendorId)"
        >
          <div class="py-2 pr-1 flex justify-between w-full">
            <span
              class="text-2xl font-normal"
              :class="[!invoice.cancelled ? '' : 'text-gray-600']"
            >
              {{ invoice.vendorName }}
            </span>
            <span
              v-if="invoice.cancelled"
              class="my-auto px-3 py-1 bg-gray-500 rounded-full text-gray-800 flex align-middle leading-none shadow"
            >
              Cancelled
            </span>
            <span
              v-else
              class="my-auto px-3 py-1 rounded-full text-gray-100 flex align-middle leading-none shadow"
              :class="[invoice.discrepancy < 0 || invoice.caseDiscrepancy < 0 ? 'bg-red-600' : 'bg-green-600']"
            >
              <template v-if="invoice.caseDiscrepancy < 0">
                <span>Need: +{{ Math.abs(invoice.caseDiscrepancy) }} Cases</span>
              </template>
              <template v-else-if="invoice.discrepancy >= 0">
                <unicon name="check" class="w-5 h-5" />
              </template>
              <template v-else>
                <span>-</span>
                <span>{{ Math.abs(invoice.discrepancy) | usd }}</span>
              </template>
            </span>
          </div>
          <transition-expand>
            <div v-if="activeInvoiceId == invoice.vendorId">
              <div class="py-2 pr-1 flex justify-between items-center" @click.stop>
                <span class="uppercase text-sm tracking-wider text-gray-600">Delivery Date</span>
                <v-date-picker
                  v-if="isAdmin"
                  :value="invoice.deliveryDate"
                  :min-date="tomorrow"
                  :is-required="true"
                  :popover="{
                    placement: 'bottom-end',
                    positionFixed: true,
                    class: 'z-50',
                    visibility: 'click'
                  }"
                  @input="date => updateDeliveryDate(invoice.vendorId, date)"
                >
                  <template #default="{ inputValue, inputEvents }">
                    <input
                      :value="inputValue"
                      v-on="inputEvents"
                      class="text-base bg-transparent border-b border-gray-400 focus:outline-none focus:border-gray-700 text-right cursor-pointer"
                      readonly
                    />
                  </template>
                </v-date-picker>
                <span v-else class="text-base">{{ formatDeliveryDate(invoice.deliveryDate) }}</span>
              </div>
              <div class="mx-1 my-1 h-px bg-gray-500 rounded-full"></div>
              <div class="overflow-y-scroll max-h-xs">
                <div
                  v-for="(value, key) in invoice"
                  :key="`product-${key}`"
                  @click.stop
                >
                  <transition-expand>
                    <div
                      v-if="
                        !invoice.cancelled &&
                        isProduct(key) &&
                        value.suggestedOrder > 0
                      "
                      class="flex justify-between"
                    >
                      <span
                        class="flex justify-items-center justify-center flex-col py-1 sm:py-0"
                      >
                        <div class="leading-tight">{{ productName(key) }}</div>
                        <div
                          class="leading-tight uppercase text-sm text-gray-600"
                        >
                          {{
                            ((productPriceByProductId(key) &&
                              productPriceByProductId(key).cost) ||
                              0) | usd
                          }}
                          &nbsp;&middot;&nbsp;Suggested:
                          {{ value.suggestedOrder || 0 }}
                          <span
                            v-if="orderDelta(value) !== 0"
                            :class="
                              orderDelta(value) > 0
                                ? 'text-green-600'
                                : 'text-red-500'
                            "
                            v-text="
                              (orderDelta(value) > 0 ? '+' : '') +
                              orderDelta(value)
                            "
                          />
                        </div>
                        <div
                          v-if="productById(key) && productById(key).unit"
                          class="leading-tight uppercase text-sm text-gray-600"
                        >
                          {{ productById(key).unit }}
                        </div>
                      </span>
                      <span
                        class="flex justify-items-center justify-center text-3xl"
                      >
                        <input-number
                          :active="true"
                          :value="value.amountOrdered + (value.adjustment || 0)"
                          @update="
                            val =>
                              updateItem({
                                vendorId: invoice.vendorId,
                                productId: key,
                                amount: val - value.amountOrdered
                              })
                          "
                        />
                      </span>
                    </div>
                  </transition-expand>
                </div>
                <div
                  v-if="!invoice.cancelled && hasZeroProducts(invoice)"
                  class="text-center py-1"
                  @click.stop
                >
                  <button
                    v-if="!showZeroProducts[invoice.vendorId]"
                    class="bg-gray-400 text-gray-700 hover:bg-gray-500 hover:text-gray-800 rounded px-4 py-3 text-sm uppercase tracking-wider focus:outline-none transition-colors"
                    @click="$set(showZeroProducts, invoice.vendorId, true)"
                  >
                    Add Products
                  </button>
                </div>
                <transition name="fade">
                  <div
                    v-if="
                      !invoice.cancelled && showZeroProducts[invoice.vendorId]
                    "
                  >
                    <div
                      v-for="(value, key) in invoice"
                      :key="`zero-${key}`"
                      @click.stop
                    >
                      <div
                        v-if="
                          isProduct(key) &&
                          value.suggestedOrder === 0 &&
                          value.amountOrdered === 0
                        "
                        class="flex justify-between"
                      >
                        <span
                          class="flex justify-items-center justify-center flex-col py-1 sm:py-0"
                        >
                          <div class="leading-tight">
                            {{ productName(key) }}
                          </div>
                          <div
                            class="leading-tight uppercase text-sm text-gray-600"
                          >
                            {{
                              ((productPriceByProductId(key) &&
                                productPriceByProductId(key).cost) ||
                                0) | usd
                            }}
                            &nbsp;&middot;&nbsp;Suggested: 0
                          </div>
                          <div
                            v-if="productById(key) && productById(key).unit"
                            class="leading-tight uppercase text-sm text-gray-600"
                          >
                            {{ productById(key).unit }}
                          </div>
                        </span>
                        <span
                          class="flex justify-items-center justify-center text-3xl"
                        >
                          <input-number
                            :active="true"
                            :value="
                              value.amountOrdered + (value.adjustment || 0)
                            "
                            @update="
                              (val) =>
                                updateItem({
                                  vendorId: invoice.vendorId,
                                  productId: key,
                                  amount: val - value.amountOrdered
                                })
                            "
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                </transition>
              </div>
              <div
                v-if="!invoice.cancelled"
                class="mx-1 my-1 h-px bg-gray-500 rounded-full"
              />

              <div
                class="py-2 pr-1 w-full text-xl flex justify-between items-center"
                @click.stop
              >
                <span>Cancel Order</span>
                <s-switch
                  :value="invoice.cancelled || false"
                  @change="val => toggleSkip(invoice.vendorId, val)"
                />
              </div>
            </div>
          </transition-expand>
        </div>
        <div class="flex flex-row justify-between mt-3">
          <s-button
            color="white"
            color-hover="gray"
            :shade="200"
            :shade-hover="200"
            text-color="gray"
            :text-shade="900"
            @click="cancel"
          >
            <span class="font-normal">Cancel</span>
          </s-button>
          <s-button
            :active="allowConfirm"
            :color="allowConfirm ? 'red' : 'gray'"
            :shade="allowConfirm ? 700 : 200"
            :shade-hover="allowConfirm ? 800 : 200"
            :text-color="allowConfirm ? 'white' : 'gray-500'"
            @click="confirm"
          >
            <span class="font-normal">Confirm</span>
          </s-button>
        </div>
      </div>

      <div
        v-if="loading === true"
        class="text-center text-4xl font-extralight tracking-tight"
      >
        sending...
      </div>
    </div>
  </modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { DateTime } from 'luxon'
import TransitionExpand from '@/components/TransitionExpand.vue'
import InputNumber from '@/components/InputNumber.vue'
import SSwitch from '@/components/SSwitch.vue'
import SButton from '@/components/Button.vue'
import { cloneDeep } from 'lodash'
export default {
  name: 'ModalSubmitOrder',
  data: function() {
    return {
      id: null,
      isInventory: false,
      isInvoice: false,
      isVendorOrder: false,
      loading: false,
      invoices: {},
      activeInvoiceId: null,
      showZeroProducts: {}

      //invalidInvoices: {},
      //validInvoices: {},
    }
  },
  components: {
    TransitionExpand,
    InputNumber,
    SSwitch,
    SButton
  },
  computed: {
    ...mapGetters(['isTodayInventoryDay']),
    ...mapGetters('user', ['isAdmin']),
    ...mapGetters('storeModule', [
      'currentOrderId',
      'currentInventoryId',
      'invoicesByOrderId',
      'invoicesByInventoryId',
      'productPriceByProductId',
      'createOrderEmailBody'
    ]),
    ...mapGetters('storeModule/vendors', ['minimumOrderByVendorId', 'minimumCasesByVendorId']),
    ...mapGetters('storeModule/products', [
      'isProduct',
      'productName',
      'productById'
    ]),
    ...mapGetters('storeModule/invoices', ['invoiceById']),
    tomorrow() {
      return DateTime.now().setZone('America/New_York').plus({ days: 1 }).startOf('day').toJSDate()
    },
    adjustmentCostTotal() {
      return this.activeInvoiceId
        ? Object.values(this.invoices[this.activeInvoiceId]).reduce(
            (accum, current) => {
              let val = 0
              try {
                val = Object.prototype.hasOwnProperty.call(
                  current,
                  'adjustmentCost'
                )
                  ? accum + current.adjustmentCost
                  : accum
              } catch {
                val = accum
              }
              return val
            },
            0
          )
        : null
    },
    allowConfirm() {
      return (
        Object.keys(this.invoices).filter((vendorId) => {
          return (
            !this.invoices[vendorId].cancelled &&
            (this.invoices[vendorId].discrepancy < 0 ||
              this.invoices[vendorId].caseDiscrepancy < 0)
          )
        }).length == 0
      )
    }
  },
  methods: {
    computeCaseDiscrepancy(vendorId) {
      const caseTotal = Object.keys(this.invoices[vendorId])
        .filter(key => this.isProduct(key))
        .reduce((accum, key) => {
          const item = this.invoices[vendorId][key]
          return accum + (item.amountOrdered || 0) + (item.adjustment || 0)
        }, 0)
      const caseDiscrepancy = caseTotal - (this.minimumCasesByVendorId(vendorId) || 0)
      this.$set(this.invoices[vendorId], 'caseDiscrepancy', caseDiscrepancy)
    },
    ...mapActions('config', ['setFabVisible', 'setDragLock']),
    ...mapActions('storeModule/invoices', ['createInvoice', 'resendEmail']),
    ...mapActions('storeModule', [
      'updateCurrentOrderItem',
      'saveOrder',
      'closeInventory'
    ]),
    ...mapActions('storeModule/invoices', { updateInvoice: 'updateItem' }),

    beforeOpen: function(event) {
      const params = event.params || {}
      this.isInvoice = params.isInvoice || false
      this.isInventory = params.isInventory || this.isTodayInventoryDay
      this.isVendorOrder = params.isVendorOrder || false
      this.id =
        params.id ||
        (this.isInventory ? this.currentInventoryId : this.currentOrderId)

      console.log({
        id: this.id,
        isInventory: this.isInventory,
        isInvoice: this.isInvoice,
        isVendorOrder: this.isVendorOrder
      })

      if (params.invoices) {
        this.invoices = cloneDeep(params.invoices)
      } else if (this.isInvoice) {
        let invoice = this.invoiceById(this.id)
        this.invoices = {
          [invoice.vendorId]: cloneDeep(this.invoiceById(this.id))
        }
      } else {
        this.invoices = this.isInventory
          ? this.invoicesByInventoryId(this.id)
          : this.invoicesByOrderId(this.id)
      }
      console.log(this.invoices)

      Object.keys(this.invoices).forEach((vendorId) => {
        // updateAmountOrdered for each valid product with an adjustment
        if (this.isInvoice) {
          Object.keys(this.invoices[vendorId])
            .filter((key) => this.isProduct(key))
            .forEach((productId) => {
              if (
                Object.prototype.hasOwnProperty.call(
                  this.invoices[vendorId][productId],
                  'adjustment'
                )
              ) {
                this.invoices[vendorId][productId].amountOrdered -=
                  this.invoices[vendorId][productId].adjustment
              }
            })
        }

        let total = Object.values(this.invoices[vendorId]).reduce(
          (accum, current) => {
            let val = 0
            try {
              val = Object.prototype.hasOwnProperty.call(
                current,
                'adjustmentCost'
              )
                ? accum + current.adjustmentCost
                : accum
            } catch {
              val = accum
            }
            return val
          },
          0
        )
        let discrepancy =
          this.invoices[vendorId].previousCostTotal +
          (total || 0) -
          this.minimumOrderByVendorId(vendorId)
        this.$set(this.invoices[vendorId], 'discrepancy', discrepancy)
        this.$set(this.invoices[vendorId], 'cancelled', false)
        this.$set(this.showZeroProducts, vendorId, this.isVendorOrder)
        this.computeCaseDiscrepancy(vendorId)
      })

      if (this.isVendorOrder && Object.keys(this.invoices).length > 0) {
        this.activeInvoiceId = Object.keys(this.invoices)[0]
      }
      // this.invalidInvoices = Object.keys(invoices)
      //   .filter(
      //     key =>
      //       invoices[key].previousCostTotal <
      //       this.minimumOrderByVendorId(invoices[key].vendorId)
      //   )
      //   .reduce((res, key) => ((res[key] = invoices[key]), res), {})
      // this.validInvoices = Object.keys(invoices)
      //   .filter(
      //     key =>
      //       !Object.prototype.hasOwnProperty.call(this.invalidInvoices, key)
      //   )
      //   .reduce((res, key) => ((res[key] = invoices[key]), res), {})
      this.setFabVisible(false)
      this.setDragLock(true)
    },
    beforeClose: function() {
      this.id = null
      this.isInventory = false
      this.isInvoice = false
      this.isVendorOrder = false
      this.loading = false
      this.invoices = {}
      this.activeInvoiceId = null
      this.showZeroProducts = {}
      this.setFabVisible(true)
      this.setDragLock(false)
    },
    orderDelta(value) {
      const current = value.amountOrdered + (value.adjustment || 0)
      return current - (value.suggestedOrder || 0)
    },
    hasZeroProducts(invoice) {
      return Object.keys(invoice).some(
        (key) =>
          this.isProduct(key) &&
          invoice[key].suggestedOrder === 0 &&
          invoice[key].amountOrdered === 0
      )
    },
    cancel: function() {
      this.$modal.hide('modal--submit-order')
    },
    confirm: async function() {
      if (!this.allowConfirm) return
      this.loading = true

      Object.keys(this.invoices).forEach((vendorId) => {
        // do nothing with cancelled orders (no invoice will be created)
        if (this.invoices[vendorId].cancelled) return

        let anyOrdered = false

        // updateAmountOrdered for each valid product with an adjustment
        Object.keys(this.invoices[vendorId])
          .filter((key) => this.isProduct(key))
          .forEach((productId) => {
            if (
              Object.prototype.hasOwnProperty.call(
                this.invoices[vendorId][productId],
                'adjustment'
              )
            ) {
              this.invoices[vendorId][productId].amountOrdered += this.invoices[
                vendorId
              ][productId].adjustment
            }
            if (this.invoices[vendorId][productId].amountOrdered > 0)
              anyOrdered = true
          })

        if (anyOrdered == false) return

        // create and save email body for this invoice
        this.invoices[vendorId].email = this.createOrderEmailBody(vendorId)(
          this.invoices[vendorId]
        )

        // create this invoice in firestore (automatically sends invoice email)
        if (this.isInvoice) {
          if (this.invoices[vendorId].status == 'resend') {
            this.invoices[vendorId].status = 'ordered'
          }
          this.updateInvoice({
            id: this.invoices[vendorId].id,
            data: this.invoices[vendorId]
          }).then(this.resendEmail(this.invoices[vendorId].id))
        } else {
          this.createInvoice({
            ...this.invoices[vendorId]
          })
        }
      })

      // If not showing validInvoices
      // Object.keys(this.validInvoices).forEach(vendorId =>
      //   this.createInvoice({ ...this.validInvoices[vendorId] })
      // )

      // save the order (skip for vendor-based orders which have no backing order doc)
      if (!this.isInvoice && !this.isVendorOrder) {
        this.isInventory
          ? this.closeInventory(this.id)
          : this.saveOrder(this.id)
      }

      this.$modal.hide('modal--submit-order')
      this.loading = false
    },
    invoiceClick: function(id) {
      if (this.activeInvoiceId == id) this.activeInvoiceId = null
      else this.activeInvoiceId = id
    },
    toggleSkip: function(vendorId, value) {
      // if (
      //   Object.prototype.hasOwnProperty.call(
      //     this.invoices[vendorId],
      //     'cancelled'
      //   )
      // )
      //   this.$set(this.invoices[vendorId], 'cancelled', value)
      // else {
      //   this.$set(this.invoices[vendorId], 'cancelled', true)
      // }
      this.$set(this.invoices[vendorId], 'cancelled', value)
      //if (this.invoices[vendorId].cancelled == true) this.activeInvoiceId = null
    },
    formatDeliveryDate(date) {
      try {
        return DateTime.fromJSDate(date).setZone('America/New_York').toFormat('cccc, LLLL d')
      } catch {
        return '—'
      }
    },
    updateDeliveryDate(vendorId, date) {
      if (!date) return
      this.$set(this.invoices[vendorId], 'deliveryDate', date)
    },
    updateItem: function({ vendorId, productId, amount }) {
      this.$set(this.invoices[vendorId][productId], 'adjustment', amount)
      this.$set(
        this.invoices[vendorId][productId],
        'adjustmentCost',
        amount * this.invoices[vendorId][productId].previousCost
      )
      let total = Object.values(this.invoices[vendorId]).reduce(
        (accum, current) => {
          let val = 0
          try {
            val = Object.prototype.hasOwnProperty.call(
              current,
              'adjustmentCost'
            )
              ? accum + current.adjustmentCost
              : accum
          } catch {
            val = accum
          }
          // console.log(val)
          return val
        },
        0
      )
      // console.log(total)
      let discrepancy =
        this.invoices[vendorId].previousCostTotal +
        (total || 0) -
        this.minimumOrderByVendorId(vendorId)
      this.$set(this.invoices[vendorId], 'adjustmentCostTotal', total)
      this.$set(this.invoices[vendorId], 'discrepancy', discrepancy)
      this.computeCaseDiscrepancy(vendorId)
      // console.log(productId, amount)
    }
  }
}
</script>

<style scoped>
.fade-enter-active {
  transition: opacity 0.15s ease;
}
.fade-enter {
  opacity: 0;
}
</style>
